/* eslint-disable react/no-deprecated */
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { ThemeProvider } from '@material-ui/core';
import { createMuiTheme } from '@material-ui/core/styles';
import { Settings } from 'luxon';
import { HelmetProvider } from 'react-helmet-async';
import SimpleReactLightbox from 'simple-react-lightbox';
import { LANGUAGE, SENTRY_ENVIRONMENT } from '~/config/env';
import * as Sentry from '@sentry/react';
import sentryConfig from './app/config/sentry.json';
import { ThemeProvider as ThemeProviderMUI } from '@mui/material/styles';

import App from './App';
import ErrorFallback from './app/components/ErrorFallback/ErrorFallback';
import { isExpectedErrorKey } from './app/services/ApiErrorHandler';
import store from './app/reducers/createStore';
import './app/styles/styles.tsx';
import reportWebVitals from './reportWebVitals';
import './app/services/firebase';

import theme from './app/config/theme';
declare global {
  interface Window {
    newrelic: unknown;
    idwSDKWeb: (config: {
      token?: string;
      onRender?: () => void;
      onComplete?: (data: { token: string }) => void;
      onError?: (error: string) => void;
    }) => void;
    __REDUX_DEVTOOLS_EXTENSION__: unknown;
  }
}

window.newrelic = window.newrelic || {};

const enableReplays = sentryConfig.enableReplays !== false;

Sentry.init({
  dsn: sentryConfig.dsn,
  enabled: sentryConfig.isSentryEnabled !== false && !import.meta.env.DEV,
  environment: SENTRY_ENVIRONMENT,
  sendDefaultPii: true,
  enableLogs: true,
  tracesSampleRate: 0.2,
  replaysSessionSampleRate: enableReplays ? 1 : 0,
  replaysOnErrorSampleRate: enableReplays ? 1 : 0,
  beforeSend(event, hint) {
    const rawValue = event.exception?.values?.[0]?.value;
    if (
      typeof rawValue === 'string' &&
      rawValue.startsWith('Non-Error promise rejection captured with value')
    ) {
      const extracted = rawValue
        .replace(/^.*captured with value:\s*/, '')
        .trim();
      if (event.exception?.values?.[0]) {
        event.exception.values[0].type = 'UnhandledRejection';
        event.exception.values[0].value = extracted || 'unknown-non-error';
      }
      event.fingerprint = [
        'unhandled-rejection-non-error',
        extracted.slice(0, 80),
      ];
      event.level = 'warning';
    }

    const message =
      event.exception?.values?.[0]?.value ??
      (hint.originalException instanceof Error
        ? hint.originalException.message
        : undefined) ??
      event.message;
    if (typeof message === 'string' && isExpectedErrorKey(message)) {
      return { ...event, level: 'warning' };
    }
    return event;
  },
  integrations: [
    Sentry.browserTracingIntegration(),
    ...(enableReplays
      ? [
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
            maskAllInputs: false,
          }),
        ]
      : []),
    Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
  ],
});

Settings.defaultLocale = LANGUAGE;

const appTheme = createMuiTheme(theme);

ReactDOM.render(
  <React.StrictMode>
    <Provider store={store}>
      <HelmetProvider>
        <ThemeProviderMUI theme={appTheme}>
          <ThemeProvider theme={appTheme}>
            <SimpleReactLightbox>
              <Sentry.ErrorBoundary
                fallback={({ error, resetError }) => (
                  <ErrorFallback error={error} resetError={resetError} />
                )}
              >
                <App />
              </Sentry.ErrorBoundary>
            </SimpleReactLightbox>
          </ThemeProvider>
        </ThemeProviderMUI>
      </HelmetProvider>
    </Provider>
  </React.StrictMode>,
  document.getElementById('root')
);

reportWebVitals();
